import type { ReactNode, ButtonHTMLAttributes, HTMLAttributes } from "react";

export function Card({
  children,
  className = "",
  accent = false,
  ...rest
}: HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  className?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl border bg-panel/70 backdrop-blur p-5 transition-colors duration-200 ${
        accent
          ? "border-plasma/30 shadow-glow-sm"
          : "border-line hover:border-plasma/20"
      } ${className}`}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Button({
  children,
  variant = "primary",
  className = "",
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "danger";
}) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-all duration-150 disabled:opacity-40 disabled:cursor-not-allowed";
  const styles =
    variant === "primary"
      ? "bg-gradient-to-r from-plasma to-plasma2 text-void shadow-glow hover:brightness-110 hover:shadow-glow active:scale-[0.98]"
      : variant === "danger"
        ? "bg-danger/90 text-white hover:brightness-110 active:scale-[0.98]"
        : "border border-line text-ink hover:border-plasma/50 hover:text-plasma/90 transition-colors";
  return (
    <button className={`${base} ${styles} ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="text-sm font-medium text-ink">{label}</span>
      {children}
      {error ? (
        <span className="block text-xs text-danger">{error}</span>
      ) : hint ? (
        <span className="block text-xs text-muted">{hint}</span>
      ) : null}
    </label>
  );
}

export const inputClass =
  "w-full rounded-lg border border-line bg-panel2 px-3 py-2 text-sm text-ink placeholder:text-muted/60 focus:border-plasma focus:shadow-glow-sm outline-none transition-colors duration-150";

export function Pill({
  children,
  tone = "muted",
}: {
  children: ReactNode;
  tone?: "muted" | "warn" | "plasma" | "danger";
}) {
  const tones = {
    muted: "border-line/80 text-muted bg-line/25",
    warn: "border-warn/40 text-warn bg-warn/10",
    plasma: "border-plasma/40 text-plasma bg-plasma/10",
    danger: "border-danger/40 text-danger bg-danger/10",
  } as const;
  return (
    <span
      className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${tones[tone]}`}
    >
      {children}
    </span>
  );
}
